import { inject, Service, Injector, NgZone, runInInjectionContext } from '@angular/core';
import { getToken, Messaging, onMessage } from '@angular/fire/messaging';
import { Router } from '@angular/router';
import { ENotificationActionKey } from 'app/core/enums/communication.enum';
import { UrlHelper } from 'app/core/helpers/url.helper';
import { environment } from '../../../../environments/environment';

@Service()
export class FirebaseMessagingService {
    private readonly messaging = inject(Messaging);
    private readonly router = inject(Router);
    private readonly urlHelper = inject(UrlHelper);
    private readonly ngZone = inject(NgZone);
    private readonly injector = inject(Injector);

    constructor() {
        this.setupServiceWorkerCommunication();
    }

    private setupServiceWorkerCommunication(): void {
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.getRegistrations().then((registrations) => {
                if (registrations.length > 0) {
                    navigator.serviceWorker.addEventListener('message', (event) => {
                        if (event.data?.type === 'NOTIFICATION_CLICK') {
                            this.ngZone.run(() => {
                                this.handleNotificationClick(event.data.clickAction);
                            });
                        }
                    });
                }
            });
        }
    }

    public listenToMessages(): void {
        onMessage(this.messaging, (payload) => {
            if (Notification.permission === 'granted') {
                const clickAction = this.generateClickAction(payload);
                const title = payload.data?.title || '';
                const body = payload.data?.body || '';

                const notification = new Notification(title, {
                    body,
                    icon: payload.data?.icon || '/assets/notification/kiwid.png',
                    data: { ...payload.data, click_action: clickAction },
                    requireInteraction: false,
                });

                notification.onclick = (event) => {
                    event.preventDefault();
                    notification.close();
                    window.focus();
                    this.handleNotificationClick(clickAction);
                };
            }
        });
    }

    private generateClickAction(payload: any): string {
        let clickAction = '/';
        if (payload.data) {
            try {
                const key = payload.data.key;
                if (key === ENotificationActionKey.ORDER_CHAT) {
                    const { orderId, locationId, roomType } = JSON.parse(payload.data.property);
                    clickAction = this.urlHelper.goToOrderDetailChat(orderId);
                }
            } catch (error) {
                console.error('[Firebase] Erro ao processar dados da notificação:', error);
            }
        }
        return clickAction;
    }

    private handleNotificationClick(clickAction: string): void {
        if (clickAction.startsWith('http://') || clickAction.startsWith('https://')) {
            if (clickAction.includes(window.location.origin)) {
                const route = clickAction.replace(window.location.origin, '');
                this.router.navigateByUrl(route);
            } else {
                window.open(clickAction, '_blank');
            }
        } else {
            this.router.navigateByUrl(clickAction);
        }
    }

    public async requestPermission(): Promise<string> {
        if (Notification.permission === 'granted') {
            const registrations = await navigator.serviceWorker.getRegistrations();
            return this.ngZone.run(() => this.getToken(registrations));
        }

        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
            const registrations = await navigator.serviceWorker.getRegistrations();
            return this.ngZone.run(() => this.getToken(registrations));
        }
        throw new Error('Without permission');
    }

    private async getToken(registrations: ReadonlyArray<ServiceWorkerRegistration>): Promise<string> {
        return runInInjectionContext(this.injector, () =>
            getToken(this.messaging, {
                vapidKey: environment.firebase.vapidKey,
                serviceWorkerRegistration: registrations.at(0),
            }),
        );
    }
}
